import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDown, Search, Check } from 'lucide-react';

/**
 * Compact searchable dropdown for the forecast header.
 *
 * Same interaction contract as the app's `components/SearchableSelect` — type
 * to filter, arrow keys to move, Enter to pick, Escape to close, click-outside
 * to dismiss — but sized and coloured for a 32px header chip instead of a form
 * field. The shared one is `w-full px-3 py-2` with gray borders and blue focus
 * rings; dropping it in here would have put a form control in the middle of a
 * toolbar.
 *
 * The search box only appears once the list is long enough to need it: a
 * property list of three does not deserve a text input above it.
 */

export interface HeaderSelectOption {
  id: number | string;
  label: string;
  /** Optional trailing hint (e.g. a year, a status) shown dimmed in the list. */
  hint?: string;
}

interface HeaderSelectProps {
  options: HeaderSelectOption[];
  value: number | string | null | undefined;
  onChange: (id: number | string) => void;
  icon?: React.ReactNode;
  /** Truncation budget for the trigger label — the header shrinks this as space runs out. */
  maxLabelWidth?: number;
  placeholder?: string;
  ariaLabel?: string;
  /** Show the filter box from this many options up. */
  searchThreshold?: number;
  className?: string;
}

export function HeaderSelect({
  options,
  value,
  onChange,
  icon,
  maxLabelWidth = 200,
  placeholder = 'Select',
  ariaLabel,
  searchThreshold = 7,
  className = '',
}: HeaderSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlighted, setHighlighted] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const selected = options.find(o => o.id === value) || null;
  const showSearch = options.length >= searchThreshold;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(o => o.label.toLowerCase().includes(q) || String(o.hint || '').toLowerCase().includes(q));
  }, [options, query]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) close();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Open with the current selection highlighted, not the first row — arrowing
  // from "wherever you already are" is what a select does.
  useEffect(() => {
    if (!open) return;
    const idx = filtered.findIndex(o => o.id === value);
    setHighlighted(idx >= 0 ? idx : 0);
    if (showSearch) searchRef.current?.focus();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep the highlighted row in view while arrowing through a long list
  useEffect(() => {
    if (!open || !listRef.current) return;
    const node = listRef.current.children[highlighted] as HTMLElement | undefined;
    node?.scrollIntoView({ block: 'nearest' });
  }, [highlighted, open]);

  function close() {
    setOpen(false);
    setQuery('');
  }

  function pick(option: HeaderSelectOption) {
    onChange(option.id);
    close();
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (!open) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlighted(i => Math.min(i + 1, filtered.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlighted(i => Math.max(i - 1, 0));
        break;
      case 'Home':
        e.preventDefault();
        setHighlighted(0);
        break;
      case 'End':
        e.preventDefault();
        setHighlighted(filtered.length - 1);
        break;
      case 'Enter':
        e.preventDefault();
        if (filtered[highlighted]) pick(filtered[highlighted]);
        break;
      case 'Escape':
        e.preventDefault();
        close();
        break;
      default:
        break;
    }
  }

  return (
    <div ref={rootRef} className={`relative flex-none ${className}`}>
      <button
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => (open ? close() : setOpen(true))}
        onKeyDown={onKeyDown}
        className="flex items-center gap-2 h-8 px-3 rounded-lg transition-colors min-w-0 max-w-full"
        style={{ background: open ? 'var(--lodge-onyx-100)' : 'var(--lodge-onyx-50)' }}
        onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--lodge-onyx-100)'; }}
        onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = open ? 'var(--lodge-onyx-100)' : 'var(--lodge-onyx-50)'; }}
        title={selected?.label || placeholder}
      >
        {icon}
        <span
          className="text-sm font-semibold truncate"
          style={{ color: selected ? 'var(--lodge-onyx-800)' : 'var(--lodge-onyx-400)', maxWidth: maxLabelWidth }}
        >
          {selected?.label || placeholder}
        </span>
        <ChevronDown
          size={14}
          className="flex-none transition-transform"
          style={{ color: 'var(--lodge-onyx-400)', transform: open ? 'rotate(180deg)' : undefined }}
        />
      </button>

      {open && (
        <div
          className="absolute top-full left-0 mt-1 z-[9999] rounded-lg overflow-hidden"
          style={{
            background: 'var(--lodge-white)',
            border: '1px solid var(--lodge-onyx-100)',
            boxShadow: 'var(--shadow-lg)',
            minWidth: Math.max(maxLabelWidth + 56, 220),
            maxWidth: 360,
          }}
          role="listbox"
        >
          {showSearch && (
            <div className="p-2" style={{ borderBottom: '1px solid var(--lodge-onyx-50)' }}>
              <div className="relative">
                <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2" style={{ color: 'var(--lodge-onyx-400)' }} />
                <input
                  ref={searchRef}
                  value={query}
                  onChange={e => { setQuery(e.target.value); setHighlighted(0); }}
                  onKeyDown={onKeyDown}
                  placeholder="Search…"
                  className="w-full h-7 pl-7 pr-2 text-sm rounded-md focus:outline-none"
                  style={{ background: 'var(--lodge-onyx-50)', color: 'var(--lodge-onyx-800)', border: '1px solid transparent' }}
                  onFocus={e => (e.currentTarget.style.borderColor = 'var(--lodge-orange-300)')}
                  onBlur={e => (e.currentTarget.style.borderColor = 'transparent')}
                />
              </div>
            </div>
          )}

          <div ref={listRef} className="overflow-y-auto py-1" style={{ maxHeight: 260 }}>
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-sm" style={{ color: 'var(--lodge-onyx-400)' }}>
                No matches
              </div>
            ) : (
              filtered.map((option, index) => {
                const isSelected = option.id === value;
                const isHighlighted = index === highlighted;
                return (
                  <button
                    key={option.id}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => pick(option)}
                    onMouseEnter={() => setHighlighted(index)}
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left transition-colors"
                    style={{
                      background: isHighlighted ? 'var(--lodge-onyx-50)' : 'transparent',
                      color: isSelected ? 'var(--lodge-orange-600)' : 'var(--lodge-onyx-700)',
                      fontWeight: isSelected ? 600 : 400,
                    }}
                  >
                    <Check
                      size={13}
                      className="flex-none"
                      style={{ opacity: isSelected ? 1 : 0, color: 'var(--lodge-orange-500)' }}
                    />
                    <span className="truncate">{option.label}</span>
                    {option.hint && (
                      <span className="ml-auto text-xs flex-none" style={{ color: 'var(--lodge-onyx-400)' }}>
                        {option.hint}
                      </span>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
